/**
 * SourceChart - Pie chart showing lead sources distribution
 */

import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const SOURCE_COLORS = {
  website: '#3B82F6',
  referral: '#10B981',
  social_media: '#8B5CF6',
  walk_in: '#F59E0B',
  phone_inquiry: '#EF4444',
  email_inquiry: '#06B6D4',
  ad_campaign: '#EC4899',
  event: '#14B8A6',
  import: '#6B7280',
  other: '#9CA3AF',
};

const SOURCE_LABELS = {
  website: 'Website',
  referral: 'Referral',
  social_media: 'Social Media',
  walk_in: 'Walk-in',
  phone_inquiry: 'Phone Inquiry',
  email_inquiry: 'Email Inquiry',
  ad_campaign: 'Ad Campaign',
  event: 'Event',
  import: 'Import',
  other: 'Other',
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-medium text-gray-900">{data.name}</p>
        <p className="text-sm text-gray-600">Leads: {data.value}</p>
        <p className="text-sm text-gray-600">
          Percentage: {data.percentage?.toFixed(1)}%
        </p>
        {data.converted !== undefined && (
          <p className="text-sm text-gray-600">
            Converted: {data.converted} ({data.conversionRate?.toFixed(1)}%)
          </p>
        )}
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }) => {
  if (!payload) return null;

  return (
    <ul className="flex flex-wrap justify-center gap-3 mt-4">
      {payload.map((entry, index) => (
        <li key={`legend-${index}`} className="flex items-center text-sm">
          <span
            className="w-3 h-3 rounded-full mr-1.5"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-600">{entry.value}</span>
        </li>
      ))}
    </ul>
  );
};

const SourceChart = ({ data = [], isLoading = false, showConversion = false }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No source data available
      </div>
    );
  }

  // Transform data for the pie chart
  const chartData = data.map((item) => ({
    name: SOURCE_LABELS[item.source] || item.source,
    value: item.count,
    source: item.source,
    converted: item.converted || 0,
    conversionRate: item.conversionRate || 0,
    percentage: item.percentage || 0,
    fill: SOURCE_COLORS[item.source] || '#6B7280',
  }));

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
        </PieChart>
      </ResponsiveContainer>

      {/* Conversion Summary Table */}
      {showConversion && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Conversion by Source</h4>
          <div className="space-y-2">
            {chartData
              .sort((a, b) => b.conversionRate - a.conversionRate)
              .map((item) => (
                <div
                  key={item.source}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center">
                    <span
                      className="w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: item.fill }}
                    />
                    <span className="text-gray-600">{item.name}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-500">{item.value} leads</span>
                    <span
                      className={`font-medium ${
                        item.conversionRate >= 30
                          ? 'text-green-600'
                          : item.conversionRate >= 15
                          ? 'text-yellow-600'
                          : 'text-gray-600'
                      }`}
                    >
                      {item.conversionRate.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceChart;
